import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Plus, Edit, Trash2, User, Mail, FileText, Clock, 
  UserPlus, CheckCircle, XCircle, AlertCircle, Eye, Ban, RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const WritersTab = () => {
  const [writers, setWriters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [selectedWriter, setSelectedWriter] = useState(null);
  const [writerArticles, setWriterArticles] = useState([]);
  
  // Invite form
  const [inviteForm, setInviteForm] = useState({ name: "", email: "" });
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // Edit form
  const [editForm, setEditForm] = useState({ headshot_url: "", bio: "" });

  useEffect(() => {
    fetchWriters();
  }, []);

  const fetchWriters = async () => {
    try {
      const res = await axios.get(`${API}/news/admin/writers`, {
        headers: getAuthHeader()
      });
      setWriters(res.data);
    } catch (err) {
      console.error("Error fetching writers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteError("");
    setInviteSuccess(false);

    try {
      await axios.post(`${API}/news/admin/writers/invite`, inviteForm, {
        headers: getAuthHeader()
      });
      setInviteSuccess(true);
      setInviteForm({ name: "", email: "" });
      fetchWriters();
      setTimeout(() => {
        setShowInvite(false);
        setInviteSuccess(false);
      }, 2000);
    } catch (err) {
      setInviteError(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateStatus = async (writerId, status) => {
    const action = status === "suspended" ? "suspend" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${action} this writer?`)) return;

    try {
      await axios.put(
        `${API}/news/admin/writers/${writerId}/status?status=${status}`,
        {},
        { headers: getAuthHeader() }
      );
      fetchWriters();
    } catch (err) {
      console.error("Error updating writer status:", err);
    }
  };

  const handleRemoveWriter = async (writerId) => {
    if (!window.confirm("Remove this writer? Their articles will remain but they will lose access.")) return;

    try {
      await axios.delete(`${API}/news/admin/writers/${writerId}`, {
        headers: getAuthHeader()
      });
      fetchWriters();
      if (selectedWriter?.id === writerId) {
        setSelectedWriter(null);
      }
    } catch (err) {
      console.error("Error removing writer:", err);
    }
  };

  const handleRevokeInvite = async (writerId) => {
    if (!window.confirm("Revoke this invite? The writer will no longer be able to sign up.")) return;
    try {
      await axios.post(`${API}/news/admin/writers/${writerId}/revoke-invite`, {}, {
        headers: getAuthHeader()
      });
      fetchWriters();
    } catch (err) {
      console.error("Error revoking invite:", err);
    }
  };

  const handleResendInvite = async (writerId) => {
    try {
      await axios.post(`${API}/news/admin/writers/${writerId}/resend-invite`, {}, {
        headers: getAuthHeader()
      });
      fetchWriters();
      alert("Invite resent successfully!");
    } catch (err) {
      console.error("Error resending invite:", err);
    }
  };

  const handleSelectWriter = async (writer) => {
    setSelectedWriter(writer);
    setEditForm({
      headshot_url: writer.headshot_url || "",
      bio: writer.bio || ""
    });

    // Fetch writer's articles
    try {
      const res = await axios.get(`${API}/news/admin/writers/${writer.id}/articles`, {
        headers: getAuthHeader()
      });
      setWriterArticles(res.data);
    } catch (err) {
      console.error("Error fetching writer articles:", err);
      setWriterArticles([]);
    }
  };

  const handleUpdateWriter = async () => {
    try {
      await axios.put(
        `${API}/news/admin/writers/${selectedWriter.id}?headshot_url=${encodeURIComponent(editForm.headshot_url)}&bio=${encodeURIComponent(editForm.bio)}`,
        {},
        { headers: getAuthHeader() }
      );
      fetchWriters();
      setSelectedWriter({ ...selectedWriter, ...editForm });
    } catch (err) {
      console.error("Error updating writer:", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 text-xs font-bold uppercase">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case "invited":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs font-bold uppercase">
            <Clock className="w-3 h-3" />
            Invited
          </span>
        );
      case "suspended":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-500 text-xs font-bold uppercase">
            <Ban className="w-3 h-3" />
            Suspended
          </span>
        );
      case "revoked":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-zinc-500/20 text-zinc-500 text-xs font-bold uppercase">
            <XCircle className="w-3 h-3" />
            Revoked
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">
            Writers
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            {writers.length} writers • {writers.filter(w => w.status === "active").length} active
          </p>
        </div>
        <Button
          onClick={() => setShowInvite(true)}
          className="bg-white text-black hover:bg-zinc-200"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Writer
        </Button>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-white/10 w-full max-w-md p-6"
          >
            <h3 className="font-heading text-xl font-bold uppercase text-white mb-4">
              Invite Writer
            </h3>

            {inviteSuccess ? (
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-green-500">Invitation sent successfully!</span>
              </div>
            ) : (
              <form onSubmit={handleInvite} className="space-y-4">
                {inviteError && (
                  <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-500">{inviteError}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    required
                    className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowInvite(false);
                      setInviteError("");
                    }}
                    className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={inviting}
                    className="flex-1 bg-white text-black hover:bg-zinc-200"
                  >
                    {inviting ? "Sending..." : "Send Invitation"}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Writers List */}
        <div className="lg:col-span-2 space-y-3">
          {writers.length === 0 ? (
            <div className="bg-zinc-900/50 border border-white/10 p-8 text-center">
              <User className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 mb-4">No writers yet</p>
              <Button
                onClick={() => setShowInvite(true)}
                className="bg-zinc-800 text-white hover:bg-zinc-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Your First Writer
              </Button>
            </div>
          ) : (
            writers.map((writer) => (
              <div
                key={writer.id}
                onClick={() => handleSelectWriter(writer)}
                className={`bg-zinc-900/50 border p-4 cursor-pointer transition-colors ${
                  selectedWriter?.id === writer.id
                    ? "border-white/30"
                    : "border-white/5 hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  {writer.headshot_url ? (
                    <img
                      src={writer.headshot_url}
                      alt={writer.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center">
                      <User className="w-6 h-6 text-zinc-500" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">{writer.name}</h3>
                      {getStatusBadge(writer.status)}
                    </div>
                    <p className="text-zinc-500 text-sm flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {writer.email}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="text-right">
                    <p className="text-white font-bold">{writer.article_count || 0}</p>
                    <p className="text-zinc-500 text-xs">articles</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Writer Details Panel */}
        <div className="lg:col-span-1">
          {selectedWriter ? (
            <div className="bg-zinc-900/50 border border-white/10 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-bold uppercase text-white">
                  Writer Details
                </h3>
                <button
                  onClick={() => setSelectedWriter(null)}
                  className="text-zinc-500 hover:text-white"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              {/* Profile */}
              <div className="text-center mb-6">
                {selectedWriter.headshot_url ? (
                  <img
                    src={selectedWriter.headshot_url}
                    alt={selectedWriter.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-3"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3">
                    <User className="w-10 h-10 text-zinc-500" />
                  </div>
                )}
                <h4 className="text-white font-bold">{selectedWriter.name}</h4>
                <p className="text-zinc-500 text-sm">{selectedWriter.email}</p>
                <div className="mt-2">{getStatusBadge(selectedWriter.status)}</div>
              </div>

              {/* Edit Form */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Headshot URL
                  </label>
                  <input
                    type="url"
                    value={editForm.headshot_url}
                    onChange={(e) => setEditForm({ ...editForm, headshot_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                    Bio
                  </label>
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                    rows={3}
                    placeholder="Writer bio..."
                    className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30 resize-none"
                  />
                </div>
                <Button
                  onClick={handleUpdateWriter}
                  className="w-full bg-zinc-800 text-white hover:bg-zinc-700 text-sm"
                >
                  Update Profile
                </Button>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-4 border-t border-white/10" data-testid="writer-actions">
                {selectedWriter.status === "invited" && (
                  <>
                    <Button
                      onClick={() => handleRevokeInvite(selectedWriter.id)}
                      className="w-full bg-yellow-600 text-black hover:bg-yellow-500 text-sm"
                      data-testid="revoke-invite-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Revoke Invite
                    </Button>
                    <Button
                      onClick={() => handleResendInvite(selectedWriter.id)}
                      className="w-full bg-zinc-800 text-white hover:bg-zinc-700 text-sm"
                      data-testid="resend-invite-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Invite
                    </Button>
                  </>
                )}
                {selectedWriter.status === "revoked" && (
                  <Button
                    onClick={() => handleResendInvite(selectedWriter.id)}
                    className="w-full bg-green-600 text-white hover:bg-green-500 text-sm"
                    data-testid="reinvite-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Re-Invite Writer
                  </Button>
                )}
                {selectedWriter.status === "active" && (
                  <Button
                    onClick={() => handleUpdateStatus(selectedWriter.id, "suspended")}
                    className="w-full bg-yellow-600 text-black hover:bg-yellow-500 text-sm"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Suspend Writer
                  </Button>
                )}
                {selectedWriter.status === "suspended" && (
                  <Button
                    onClick={() => handleUpdateStatus(selectedWriter.id, "active")}
                    className="w-full bg-green-600 text-white hover:bg-green-500 text-sm"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reactivate Writer
                  </Button>
                )}
                <Button
                  onClick={() => handleRemoveWriter(selectedWriter.id)}
                  className="w-full bg-red-600 text-white hover:bg-red-500 text-sm"
                  data-testid="remove-writer-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Writer
                </Button>
              </div>

              {/* Recent Articles */}
              {writerArticles.length > 0 && (
                <div className="mt-6 pt-4 border-t border-white/10">
                  <h4 className="text-xs font-bold uppercase text-zinc-400 mb-3">
                    Recent Articles
                  </h4>
                  <div className="space-y-2">
                    {writerArticles.slice(0, 5).map((article) => (
                      <div
                        key={article.id}
                        className="p-2 bg-black/30 border border-white/5"
                      >
                        <p className="text-white text-sm line-clamp-1">{article.title}</p>
                        <p className="text-zinc-500 text-xs">
                          {formatDate(article.created_at)} • {article.status}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-white/10 p-8 text-center">
              <User className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">Select a writer to view details</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default WritersTab;
